import React, { useRef, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { StatNavItem, SeasonNavItem } from './components/nav-items';
import { Heading, DataTypeToggle, dataTypeOptions } from './components/header';
import { PerformanceChartContainer } from './components/chart';

import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';

import {
    performanceChartNavSelector,
    performanceChartDataTypeSelector
} from 'store/modules/statspro/performance-chart/selectors';
import { playersSelector } from 'store/modules/statspro/player-comparison/selectors';
import { useGetCompetitionsQuery } from 'common/store/modules/api/afl-api';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { getTranslation } from 'common/react/utils/translations';
import { STATS_CATEGORY } from '../common/config/performance-stats';
import { initialiseNav } from 'common/store/modules/statspro/thunks/initialise-nav';
import { useResetToDefaults } from '../common/hooks/use-reset-to-defaults';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';

const NavWrapper = ({ children }) => (
    <div className="competition-nav competition-nav--no-margin-top filter-dropdown">
        {children}
    </div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const PerformanceChart = ({ title, type, compId, playerProviderId }) => {
    const isAFLW = PULSE.app.common.url.checkBaseRoute('aflw');

    const { competition } = useGetCompetitionsQuery(
        {},
        {
            selectFromResult: ({ data }) => ({
                competition: data?.find((item) => item.id === parseInt(compId))
            })
        }
    );

    const dispatch = useDispatch();
    const nav = useSelector(performanceChartNavSelector);
    const dataType = useSelector(performanceChartDataTypeSelector);
    const options = useCompetitionNavigationOptions(nav);
    const windowSize = useWindowSize();
    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    useEffect(() => {
        dispatch(
            initialiseNav({
                slice: 'performance-chart',
                initialValues: {
                    competitionId: compId,
                    seasonId: -1
                }
            })
        );
    }, [dispatch]);

    // usePerformanceChartNavSync(options, competition?.id);

    const playerId = playerProviderId;
    const playerIdsFromStore = useSelector(playersSelector);
    const ref = useRef();

    const playerIds = useMemo(
        () => (playerId ? { 1: playerId, 2: null } : playerIdsFromStore),
        [playerId, playerIdsFromStore]
    );

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            category: nav.category,
            season: nav.season,
            dataType
        },
        options: {
            ...options,
            dataTypes: dataTypeOptions
        },
        filtersFields: ['performancechart-stat', 'career-season', 'dataType'],
        filtersRef: ref
    });

    const { hasChangedFromDefaults } = useHasChangedFromDefaults({
        state: { nav: nav },
        initialValues: {
            category: STATS_CATEGORY.AFL_FANTASY_POINTS,
            seasonId: -1
        }
    });

    const resetToDefaults = useResetToDefaults({
        slice: 'performance-chart',
        initialValues: {
            competitionId: compId,
            seasonId: -1,
            initialParams: {
                category: STATS_CATEGORY.AFL_FANTASY_POINTS
            }
        }
    });

    return (
        <div
            className={classNames('fade-in-on-load', {
                'fade-in-on-load fade-in-on-load--stats comparison-chart__wrapper':
                    !playerId,
                'is-loaded': playerIds[1] || playerIds[2]
            })}
            ref={ref}
        >
            {playerIds[1] || playerIds[2] ? (
                <StatsSection.Main NavWrapper={NavWrapper}>
                    <StatsSection.Heading title={title} />
                    <StatsSection.Nav
                        showResponsiveItems={showResponsiveItems}
                        withScrollableFilter={!showResponsiveItems}
                    >
                        <StatsSection.NavItems>
                            <StatNavItem />
                            <SeasonNavItem
                                playerIds={playerIds}
                                isAFLW={isAFLW}
                            />
                            {hasChangedFromDefaults && !showResponsiveItems ? (
                                <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                    <button
                                        className="stats-leaders-filter-reset-button"
                                        onClick={resetToDefaults}
                                    >
                                        {getTranslation(
                                            'label.statsLeaders.filters.resetButtonText'
                                        )}
                                    </button>
                                </div>
                            ) : null}
                        </StatsSection.NavItems>
                        {hasChangedFromDefaults && showResponsiveItems ? (
                            <StatsSection.NavFilterItems hideReset={true}>
                                <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                    <button
                                        className="stats-leaders-filter-reset-button"
                                        onClick={resetToDefaults}
                                    >
                                        {getTranslation(
                                            'label.statsLeaders.filters.resetButtonText'
                                        )}
                                    </button>
                                </div>
                            </StatsSection.NavFilterItems>
                        ) : null}
                    </StatsSection.Nav>
                    <StatsSection.Content>
                        <StatsSection.ContentHeader>
                            <StatsSection.ContentHeaderHeading>
                                <Heading />
                            </StatsSection.ContentHeaderHeading>
                            <StatsSection.ContentHeaderOptions>
                                <DataTypeToggle />
                            </StatsSection.ContentHeaderOptions>
                        </StatsSection.ContentHeader>
                        <StatsSection.ContentBody>
                            <PerformanceChartContainer
                                playerIds={playerIds}
                                type={type}
                                containerRef={ref}
                                isAFLW={isAFLW}
                                competition={competition}
                            />
                        </StatsSection.ContentBody>
                    </StatsSection.Content>
                </StatsSection.Main>
            ) : null}
        </div>
    );
};

PerformanceChart.propTypes = {
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    playerProviderId: PropTypes.string,
    compId: PropTypes.string
};
